import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { flatten, type PropModel } from '../props/PropModels';

/**
 * Model postaci: rycerz z `public/models/knight.glb`.
 *
 * **Poza jest wypiekana w geometrię.** Plik ma szkielet, ale żadnej animacji,
 * więc postać i tak stoi w jednej pozie — a zwykła siatka nie potrzebuje
 * klonowania szkieletu na każdego gracza ani liczenia kości w każdej klatce.
 * Wymiary da się zmierzyć dopiero **po** nałożeniu kości: surowe wierzchołki
 * leżą w układzie wiązania, a eksport z Blendera dokłada do szkieletu obrót
 * i skalę sto. Gdy przyjdą animacje, ten wypiek ustępuje prawdziwemu
 * `SkinnedMesh` — i dopiero wtedy.
 *
 * Mierzenie, stawianie na zerze i normalizację wysokości robi `flatten`, ta
 * sama funkcja co przy drzewach i budynkach.
 */
export async function loadPawnModel(): Promise<PropModel> {
  const gltf = await new GLTFLoader().loadAsync('/models/knight.glb');
  gltf.scene.updateMatrixWorld(true);

  const baked = new THREE.Group();
  gltf.scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const geometry = object.geometry.clone();

    if (object instanceof THREE.SkinnedMesh) {
      object.skeleton.update();
      const position = geometry.getAttribute('position');
      const point = new THREE.Vector3();
      for (let i = 0; i < position.count; i++) {
        point.fromBufferAttribute(position, i);
        // Kości, potem transformacja samej siatki — to, co robi shader.
        object.applyBoneTransform(i, point);
        point.applyMatrix4(object.matrixWorld);
        position.setXYZ(i, point.x, point.y, point.z);
      }
      geometry.deleteAttribute('skinIndex');
      geometry.deleteAttribute('skinWeight');
      // Normalne z nowego kształtu: obrócone kości zostawiłyby stare w złą stronę.
      geometry.computeVertexNormals();
      baked.add(new THREE.Mesh(geometry, object.material));
      return;
    }

    geometry.applyMatrix4(object.matrixWorld);
    baked.add(new THREE.Mesh(geometry, object.material));
  });

  return flatten('knight', baked);
}
